/**
 * `JobDetail` — pagina di dettaglio di una singola professione (Job e-CF 3.0),
 * raggiungibile dalla pagina "Le mie professioni" cliccando una card.
 *
 * La struttura ricalca `CompetenceDetail` (dettaglio dell'assessment): una
 * card‑intestazione con il nome, la Proficiency e il donut della Copertura, le
 * card informative (summary/mission/deliverables/tasks/kpi) e una tabella. Qui
 * la tabella elenca le **competenze richieste** dalla professione, con il peso,
 * il Livello richiesto, il punteggio dell'utente su quella competenza e l'esito
 * (soddisfa o gap).
 *
 * La pagina serve due contesti, distinti dalla presenza del param di rotta
 * `userId`:
 *  - **personale** (`/jobs/:jobId`): il posizionamento dell'utente autenticato,
 *    da `GET /api/jobs/:id/positioning/me`; torna a "Le mie professioni";
 *  - **Team** (`/team/:userId/jobs/:jobId`, sola lettura): il posizionamento del
 *    membro indicato, da `GET /api/dashboard/users/:userId/job-positioning/:jobId`;
 *    torna alla dashboard del membro. I link alle competenze puntano al dettaglio
 *    competenza nel contesto Team.
 *
 * La resa del job caricato è delegata al componente presentazionale
 * `JobPositioningView`; qui restano solo la scelta della fonte dati, gli stati
 * di caricamento/errore/vuoto e il calcolo di `backTo`/`competenceDetailPath`/
 * sottotitolo in base al contesto.
 */
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type JobMatch } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { JobPositioningView } from '../components/JobPositioningView';
import { Button, EmptyState, Loading } from '../components/ui';

/** Etichette del tasto "indietro" per i percorsi di provenienza noti. */
const BACK_LABELS: Record<string, string> = {
  '/assessment': '← Torna al mio CV',
};

export default function JobDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId, userId } = useParams<{ jobId: string; userId?: string }>();

  // Contesto Team (sola lettura) quando la rotta include `userId`
  // (`/team/:userId/jobs/:jobId`); altrimenti contesto personale (`/jobs/:jobId`).
  const isTeamContext = userId !== undefined;

  // Provenienza opzionale propagata dal chiamante come `state.from` (es.
  // `/assessment` quando si arriva da "Il mio CV"): se presente, il tasto
  // "indietro" torna lì invece che al default ("Le mie professioni"). Vale solo
  // nel contesto personale; il contesto Team mantiene il proprio ritorno.
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const backFrom =
    !isTeamContext && typeof fromState === 'string' && fromState !== '' ? fromState : null;

  const jobQuery = useQuery<JobMatch>({
    queryKey: isTeamContext
      ? ['dashboard', 'users', userId, 'job-positioning', jobId]
      : ['jobs', 'positioning', jobId],
    queryFn: () =>
      api.get<JobMatch>(
        isTeamContext
          ? `/api/dashboard/users/${encodeURIComponent(userId ?? '')}/job-positioning/${encodeURIComponent(jobId ?? '')}`
          : `/api/jobs/${encodeURIComponent(jobId ?? '')}/positioning/me`,
      ),
    enabled: Boolean(jobId) && (!isTeamContext || Boolean(userId)),
  });

  const job = jobQuery.data ?? null;

  // Path di ritorno: alla dashboard del membro nel contesto Team; alla
  // provenienza esplicita (`state.from`, es. "Il mio CV") se presente;
  // altrimenti a "Le mie professioni".
  const backToJobs = () => {
    void navigate(
      isTeamContext
        ? `/insights/team/${encodeURIComponent(userId ?? '')}`
        : backFrom ?? '/assessment/jobs',
    );
  };

  const backLabel = isTeamContext
    ? '← Torna al membro'
    : backFrom !== null
      ? BACK_LABELS[backFrom] ?? '← Torna indietro'
      : '← Torna alle professioni';

  // Path del dettaglio competenza: nel contesto Team resta nel drill-down del
  // membro (sola lettura); altrimenti il proprio assessment.
  const competenceDetailPath = (competenceId: string) =>
    isTeamContext
      ? `/insights/team/${encodeURIComponent(userId ?? '')}/competences/${encodeURIComponent(competenceId)}`
      : `/assessment/competences/${encodeURIComponent(competenceId)}`;

  // ----- Rendering -----

  if (jobQuery.isLoading) {
    return (
      <section aria-labelledby="job-detail-title">
        <h1 id="job-detail-title">Professione</h1>
        <Loading>Caricamento della professione…</Loading>
      </section>
    );
  }

  if (jobQuery.isError || job === null) {
    return (
      <section aria-labelledby="job-detail-title">
        <h1 id="job-detail-title">Professione</h1>
        <EmptyState role="status">
          La professione richiesta non esiste o non è più disponibile.
        </EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToJobs}>
            {backLabel}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <JobPositioningView
      job={job}
      backTo={{ label: backLabel, onClick: backToJobs }}
      competenceDetailPath={competenceDetailPath}
      // Provenienza per i link alle competenze: nel contesto personale è questa
      // stessa professione (così il dettaglio competenza torna qui); nel
      // contesto Team il drill-down competenza resta nel membro col proprio
      // ritorno, quindi non si propaga alcuna provenienza.
      competenceBackTo={isTeamContext ? undefined : location.pathname}
      subtitle={
        isTeamContext
          ? 'Le competenze richieste dalla professione e il posizionamento del membro.'
          : 'Le competenze richieste dalla professione e il tuo posizionamento.'
      }
    />
  );
}
